import React, { useMemo, useRef, useState } from 'react';
import Form, { Item as FormItem } from 'devextreme-react/form';
import Button from 'devextreme-react/button';
import { RequiredRule } from 'devextreme-react/form';
import { useScreenSize } from '../../../../../utils/media-query';

type TemplateField = {
  id: number;
  key: string;
  label: string;
  editorType: string;
  required: boolean;
  defaultValue: any;
  lookupValues?: string;
};

type Props = {
  template: TemplateField[];
  onCancel: () => void;
  onSubmit: (values: any) => Promise<void> | void;
  initialValues?: any;
  readOnly?: boolean;
};

const StepForm: React.FC<Props> = ({ template, onCancel, onSubmit, initialValues = null, readOnly = false }) => {
  const formRef = useRef<any>(null);
  const [loading, setLoading] = useState(false);

  const formData = useMemo(() => {
    const d: any = {};
    (template || []).forEach((f) => (d[f.key] = f.defaultValue === '' ? null : f.defaultValue));
    // merge with provided initial values so previously entered data is shown
    if (initialValues && typeof initialValues === 'object') {
      Object.keys(initialValues).forEach((k) => {
        d[k] = initialValues[k];
      });
    }
    return d;
  }, [template]);

  const handleSave = async () => {
    try {
      setLoading(true);
      const valid = formRef.current?.instance.validate();
      if (valid && valid.isValid) {
        const values = formRef.current?.instance.option('formData');
        await onSubmit(values);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Form ref={formRef} formData={formData} labelLocation='top'>
        {template.map((field) => {
          const editorType = field.editorType || 'dxTextBox';
          const commonProps: any = { dataField: field.key, label: { text: field.label } };
          // editorOptions used to set readOnly/disabled when needed
          const editorOptions: any = {};
          if (readOnly) {
            editorOptions.readOnly = true;
            editorOptions.disabled = true;
          }
          if (editorType === 'dxLookup') {
            const values = (field.lookupValues || '')
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean);
            return (
              <FormItem key={field.id} {...commonProps} editorType={'dxLookup'} editorOptions={{ ...editorOptions, dataSource: values, valueExpr: 'this', displayExpr: (item: any) => item }}>
                {field.required && <RequiredRule />}
              </FormItem>
            );
          }
          if (editorType === 'dxCheckBox') {
            return (
              <FormItem key={field.id} {...commonProps} editorType={'dxCheckBox'} editorOptions={editorOptions}>
                {field.required && <RequiredRule />}
              </FormItem>
            );
          }
          if (editorType === 'dxDateBox') {
            return (
              <FormItem key={field.id} {...commonProps} editorType={'dxDateBox'} editorOptions={editorOptions}>
                {field.required && <RequiredRule />}
              </FormItem>
            );
          }
          return (
            <FormItem key={field.id} {...commonProps} editorType={'dxTextBox'} editorOptions={editorOptions}>
              {field.required && <RequiredRule />}
            </FormItem>
          );
        })}
      </Form>

      {/* Hide action buttons when in readOnly/view-only mode (visual only) */}
      {!readOnly && (
        <div style={{ marginTop: 'auto', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button text='Cancelar' onClick={onCancel} />
          <Button text='Guardar' type='default' onClick={handleSave} disabled={loading || readOnly} />
        </div>
      )}
    </div>
  );
};

export default StepForm;
